import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiConfig } from "@/config/api.config";
import { ApiHookProps } from '@/@types/api.types';
import { ReservationDetailsResponseDto, CreateReservationRequestDto, UpdateReservationRequestDto } from '@/features/reservations/types/reservations-dto';
import EToast from '@/components/everridge/EToast';
import { t } from 'i18next';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';

type Props = {} & ApiHookProps;

export const useReservationDetailsApi = ({id}: Props = {}) => {
  const axiosInstance = useAxiosAuth();
  const queryClient = useQueryClient();

  const {
      data: reservationDetailsData, 
      isLoading: isLoadingReservationDetailsData, 
      isError: isErrorReservationDetailsData
  } = useQuery({
      queryKey: [ApiConfig.queries.features.reservations.queryKeys.details, id],
      queryFn: async (): Promise<ReservationDetailsResponseDto> => {
          const url = `${ApiConfig.queries.features.reservations.endpoints.get}/${id}`;               
          const response = await axiosInstance.get<ReservationDetailsResponseDto>(url);
          return response.data;
      },
      enabled: !!id,
  });

  const {
    mutateAsync: createReservationData, 
    isPending: isCreatingReservationData, 
    isError: isErrorCreatingReservationData
  } = useMutation({
    mutationFn: async (dto: CreateReservationRequestDto) => {
        const response = await axiosInstance.post(
            ApiConfig.queries.features.reservations.endpoints.create, dto
        );  
        return response.data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [ApiConfig.queries.features.reservations.queryKeys.list] }),
        queryClient.invalidateQueries({ queryKey: [ApiConfig.queries.features.calendar.queryKeys.list] }),
      ]);
      EToast({
        variant: 'success',
        message: t('main.reservations.toasts.create.success.message'), 
        description: t('main.reservations.toasts.create.success.description')
      });
    },
    onError: () => {
      EToast({
        variant: 'error',
        message: t('main.reservations.toasts.create.error.message'), 
        description: t('main.reservations.toasts.create.error.description')
      });
    }
  });

  const {
    mutateAsync: updateReservationData, 
    isPending: isUpdatingReservationData, 
    isError: isErrorUpdatingReservationData
  } = useMutation({
    mutationFn: async (dto: UpdateReservationRequestDto) => {
      const url = `${ApiConfig.queries.features.reservations.endpoints.update}/${id}`;
      const response = await axiosInstance.put(url, dto);
      return response.data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [ApiConfig.queries.features.reservations.queryKeys.list] }),
        queryClient.invalidateQueries({ queryKey: [ApiConfig.queries.features.calendar.queryKeys.list] }),
      ]);
      EToast({
        variant: 'success',
        message: t('main.reservations.toasts.update.success.message'), 
        description: t('main.reservations.toasts.update.success.description')
      });  
    },
    onError: () => {
      EToast({
        variant: 'error',
        message: t('main.reservations.toasts.update.error.message'), 
        description: t('main.reservations.toasts.update.error.description')
      });
    }
  });

  const {
    mutateAsync: cancelReservation, 
    isPending: isCancelingReservation, 
    isError: isErrorCancelingReservation
  } = useMutation({
    mutationFn: async (id: string) => {
      const url = ApiConfig.queries.features.reservations.endpoints.cancel.replace('{id}', id);
      const response = await axiosInstance.patch(url);
      return response.data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [ApiConfig.queries.features.reservations.queryKeys.list] }),
        queryClient.invalidateQueries({ queryKey: [ApiConfig.queries.features.calendar.queryKeys.list] }),
      ]);
      EToast({
        variant: 'success',
        message: t('main.reservations.toasts.cancel.success.message'), 
        description: t('main.reservations.toasts.cancel.success.description')
      });  
    },
    onError: () => {
      EToast({
        variant: 'error',
        message: t('main.reservations.toasts.cancel.error.message'), 
        description: t('main.reservations.toasts.cancel.error.description')
      });
    }
  });

  return {
    reservationDetailsData,
    isLoadingReservationDetailsData,
    isErrorReservationDetailsData,
    createReservationData,
    isCreatingReservationData,
    isErrorCreatingReservationData,
    updateReservationData,
    isUpdatingReservationData,
    isErrorUpdatingReservationData,
    cancelReservation,
    isCancelingReservation,
    isErrorCancelingReservation
  }
}